/**
 * @fileoverview Table header configuration for Sunstate Urology's
 * "Orders to Process" worklist tab.
 *
 * This is an org-specific variant of the default generic auth-status worklist
 * headers (`getDefaultAuthStatusTableHeaders`). All default columns are
 * preserved; two columns are added:
 * - "Date of Creation" (sortable on `created_at`), placed immediately before
 *   "Date of Work" so the two date columns sit together with creation first.
 * - "Location", placed after "Plan Details".
 *
 * To accommodate the two extra columns within the 100% width budget, several
 * existing columns are narrowed (see `getSunstateOrdersToProcessTableHeaders`
 * docstring for the specific deltas).
 *
 * @module sunstateOrdersToProcessTableHeaders
 */

import { TableCellType } from "../../../../components/custom-table/table";
import { GenericAuthStatusTableColumnKeys } from "../../../../enums/tableColumnKeys";
import { MedicalPaOrdersAlgoliaSortFields } from "../../medicalPaOrdersAlgolia/utils/medicalPaOrdersTable";

/**
 * Returns the table header configuration for the Sunstate Urology
 * "Orders to Process" worklist tab.
 *
 * @description
 * Differences from `getDefaultAuthStatusTableHeaders`:
 * - Adds "Date of Creation" (`CREATED_AT`, sortable on `created_at`)
 *   immediately before "Date of Work", so the two date columns sit together
 *   with creation leading.
 * - Adds "Location" (`LOCATION`, non-sortable) immediately after
 *   "Plan Details".
 * - Existing column widths are tightened to keep the total at 100%:
 *   Patient Name 13 → 11, Date of Work 8 → 7, Plan Details 14 → 10,
 *   Medication 10 → 8, Provider 13 → 10, DOS 8 → 7, BO Value 10 → 7,
 *   Auth Status 10 → 7, Icons 7 → 6.
 *
 * Location is not server-sortable — no corresponding entry exists in
 * `MedicalPaOrdersAlgoliaSortFields`.
 *
 * @returns {Array<Object>} Array of table header configuration objects whose
 *   widths sum to 100
 */
export const getSunstateOrdersToProcessTableHeaders = () => {
  return [
    {
      label: "Patient Name",
      key: GenericAuthStatusTableColumnKeys.PATIENT_DETAILS,
      order: 1,
      width: 11,
      sortable: true,
      type: TableCellType.MULTILINE,
      subKey: "mainText",
      sortField: MedicalPaOrdersAlgoliaSortFields.FIRST_NAME,
    },
    {
      label: "Date of Creation",
      key: GenericAuthStatusTableColumnKeys.CREATED_AT,
      order: 2,
      width: 8,
      sortable: true,
      type: TableCellType.DATE,
      sortField: MedicalPaOrdersAlgoliaSortFields.CREATED_AT,
    },
    {
      label: "Date of Work",
      key: GenericAuthStatusTableColumnKeys.DATE_OF_WORK,
      order: 3,
      width: 7,
      sortable: true,
      type: TableCellType.DATE,
      sortField: MedicalPaOrdersAlgoliaSortFields.DATE_OF_WORK,
    },
    {
      label: "Plan Details",
      key: GenericAuthStatusTableColumnKeys.PLAN_DETAILS,
      order: 4,
      width: 10,
      sortable: true,
      type: TableCellType.MULTILINE,
      subKey: "mainText",
      sortField: MedicalPaOrdersAlgoliaSortFields.PRIMARY_PAYER_NAME,
    },
    {
      label: "Location",
      key: GenericAuthStatusTableColumnKeys.LOCATION,
      order: 5,
      width: 12,
      type: TableCellType.STRING,
    },
    {
      label: "Medication",
      key: GenericAuthStatusTableColumnKeys.MEDICATION,
      order: 6,
      width: 8,
      sortable: true,
      type: TableCellType.STRING,
      sortField: MedicalPaOrdersAlgoliaSortFields.REGIMEN_NAME,
    },
    {
      label: "Provider",
      key: GenericAuthStatusTableColumnKeys.PROVIDER,
      order: 7,
      width: 10,
      sortable: true,
      type: TableCellType.STRING,
      sortField: MedicalPaOrdersAlgoliaSortFields.PRACTITIONER_NAME,
    },
    {
      label: "DOS",
      key: GenericAuthStatusTableColumnKeys.DATE_OF_SERVICE,
      order: 8,
      width: 7,
      sortable: true,
      type: TableCellType.DATE,
      sortField: MedicalPaOrdersAlgoliaSortFields.DATE_OF_SERVICE,
    },
    {
      label: "BO Value",
      key: GenericAuthStatusTableColumnKeys.BO_VALUE,
      order: 9,
      width: 7,
      type: TableCellType.BADGE,
    },
    {
      label: "Auth Status",
      key: GenericAuthStatusTableColumnKeys.AUTH_VERIFICATION,
      order: 10,
      width: 7,
      type: TableCellType.BADGE,
    },
    {
      label: "",
      key: GenericAuthStatusTableColumnKeys.RPA_STATUS,
      order: 11,
      width: 4,
      type: TableCellType.RPA_STATUS,
    },
    {
      label: "",
      key: GenericAuthStatusTableColumnKeys.RPA_STATUS_TWO,
      order: 12,
      width: 3,
      type: TableCellType.RPA_STATUS_TWO,
    },
    {
      label: "",
      key: GenericAuthStatusTableColumnKeys.ORDER_PROCESS_AND_REVIEW_STATUS,
      order: 13,
      width: 6,
      type: TableCellType.OVERLAPPING_ICONS_WITH_POPOVER_AND_ACTIONS,
    },
  ];
};
